
import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { createApiRequest } from "@/utils/apiUtils";
import { format, startOfMonth, endOfMonth } from "date-fns";
import ScheduleDialog from "./ScheduleDialog";

interface Trip {
  id: string;
  pickup_at: string;
}

const ScheduleWidget = () => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Function to format date for API request
  const formatDateForApi = (date: Date) => {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXX");
  };
  
  // Get start and end of current month for API request
  const startDate = startOfMonth(currentMonth);
  const endDate = endOfMonth(currentMonth);
  
  // Fetch claimed trips for the current month
  const { data: tripsData, isLoading } = useQuery({
    queryKey: ["scheduledTrips", formatDateForApi(startDate), formatDateForApi(endDate)],
    queryFn: async () => {
      const request = createApiRequest(
        `trips/?type=claimed&pickup_at__gte=${formatDateForApi(startDate)}&pickup_at__lte=${formatDateForApi(endDate)}`
      );
      const response = await fetch(request);
      
      if (!response.ok) {
        throw new Error("Failed to fetch scheduled trips");
      }
      
      return response.json();
    }
  });
  
  // Extract booked dates from the trips data
  const bookedDates = tripsData?.results?.map((trip: Trip) => {
    return new Date(trip.pickup_at);
  }) || [];
  
  // Function to handle month navigation
  const handlePreviousMonth = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + 1);
      return newMonth;
    });
  };
  
  return (
    <>
      <Card className="shadow-sm bg-white text-gray-800">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-xl font-bold text-gray-900">My Schedule</CardTitle>
          <button 
            onClick={() => setDialogOpen(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <ExternalLink size={16} />
          </button>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <button 
              onClick={handlePreviousMonth} 
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <h3 className="font-medium text-gray-800">
              {format(currentMonth, "MMMM yyyy")}
            </h3>
            <button 
              onClick={handleNextMonth} 
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
          
          <div className="flex justify-center">
            <Calendar
              mode="multiple"
              selected={bookedDates}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              className="rounded-md border-0 w-full mx-auto px-0"
              classNames={{
                day_selected: "bg-neon-green text-black hover:bg-neon-green hover:text-black focus:bg-neon-green focus:text-black",
                day_today: "bg-gray-100 text-gray-900",
                head_cell: "text-gray-500 font-normal text-xs",
                cell: "h-10 w-10 text-center text-sm p-0 relative flex justify-center",
                day: "h-10 w-10 p-0 font-normal text-sm text-gray-700",
                months: "w-full",
                month: "w-full",
                caption: "hidden", // Hide the default caption that shows month/year
                table: "w-full",
                row: "flex justify-between w-full mt-2",
              }}
              disabled={isLoading}
            />
          </div>
          
          {isLoading && (
            <div className="mt-4 text-sm text-gray-500 text-center">
              Loading scheduled trips...
            </div>
          )}
        </CardContent>
      </Card>
      
      <ScheduleDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen} 
      />
    </>
  );
};

export default ScheduleWidget;
